// Standalone chrome (Nav + Footer) extracted from _v04_modernist.jsx so it
// can be mounted on interior portal pages, not just the homepage.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#f4f3ef', fg: '#0a0a0a', muted: '#5a5a5a',
  border: '#0a0a0a', accent: '#ff5b1f', accentFg: '#ffffff',
  inputBg: '#ffffff', radius: 0,
  font: '"Helvetica Neue", Helvetica, Arial, sans-serif',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const navTopLinks = [
  { label: 'Tools', href: '/tools-wissen' },
  { label: 'Marktanalyse', href: '/marktanalyse' },
  { label: 'Förderung', href: '/förderung-finder' },
  { label: 'Ratgeber', href: '/ratgeber' },
];

const navLinks = [
  { label: 'Tools', href: '/tools-wissen' },
  { label: 'Marktanalyse', href: '/marktanalyse' },
  { label: 'Förderung', href: '/förderung-finder' },
  { label: 'Ratgeber', href: '/ratgeber' },
  { label: 'Impressum', href: '/impressum' },
  { label: 'Datenschutz', href: '/datenschutz' },
];

const formatTelHref = (p) => 'tel:' + (p || '').replace(/[^0-9+]/g, '');

export function V04ModernistNav({ brand = 'Waemo', phone }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const phoneDisplay = phone || null;
  return (
    <div style={{
      padding: isMobile ? '12px 16px' : '16px 32px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      borderBottom: `2px solid ${theme.fg}`,
      background: theme.bg, color: theme.fg, fontFamily: theme.font,
      position: 'relative',
    }}>
      <div style={{ fontWeight: 900, fontSize: isMobile ? 18 : 22, letterSpacing: '-0.04em' }}>{brand}<span style={{ color: theme.accent }}>.</span></div>
      {!isMobile && (
        <>
          <div style={{ display: 'flex', gap: 24, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
            {navTopLinks.map(l => <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none' }}>{l.label}</Link>)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {phoneDisplay && (
              <a href={formatTelHref(phoneDisplay)} style={{
                fontSize: 12, fontWeight: 800, fontFamily: theme.font,
                color: theme.fg, textDecoration: 'none',
                textTransform: 'uppercase', letterSpacing: '0.08em',
                borderBottom: `2px solid ${theme.accent}`, paddingBottom: 2,
              }}>{`Tel ${phoneDisplay}`}</a>
            )}
            <Link href="/angebot-vergleichen" style={{
              padding: '9px 16px', fontSize: 12, fontWeight: 700, fontFamily: theme.font,
              background: theme.fg, color: theme.bg, border: 'none',
              textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'pointer',
              textDecoration: 'none', display: 'inline-block',
            }}>Jetzt vergleichen ↗</Link>
          </div>
        </>
      )}
      {isMobile && (
        <button
          aria-label="Menü"
          onClick={() => setIsMenuOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <HamburgerIcon open={isMenuOpen} />
        </button>
      )}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: theme.bg, borderBottom: `2px solid ${theme.fg}`,
          padding: '16px', display: 'flex', flexDirection: 'column', gap: 14,
          zIndex: 50,
        }}>
          {navTopLinks.map(l => (
            <Link key={l.label} href={l.href} style={{
              color: theme.fg, textDecoration: 'none',
              fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em',
            }}>{l.label}</Link>
          ))}
          {phoneDisplay && (
            <a href={formatTelHref(phoneDisplay)} style={{
              color: theme.fg, textDecoration: 'none',
              fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em',
              borderBottom: `2px solid ${theme.accent}`, alignSelf: 'flex-start', paddingBottom: 2,
            }}>{`Tel ${phoneDisplay}`}</a>
          )}
          <Link href="/angebot-vergleichen" style={{
            padding: '10px 16px', fontSize: 13, fontWeight: 700,
            background: theme.fg, color: theme.bg, border: 'none',
            textTransform: 'uppercase', letterSpacing: '0.04em',
            textAlign: 'center', textDecoration: 'none', display: 'block', marginTop: 4,
          }}>Jetzt vergleichen ↗</Link>
        </div>
      )}
    </div>
  );
}

export function V04ModernistFooter({ brand = 'Waemo', phone }) {
  const isMobile = useIsMobile();
  const phoneDisplay = phone || null;
  return (
    <div style={{
      background: theme.fg, color: theme.bg,
      padding: isMobile ? '24px 16px' : '40px 32px',
      fontFamily: theme.font,
    }}>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'flex-start',
        gap: isMobile ? 20 : 0,
        paddingBottom: isMobile ? 20 : 32,
        borderBottom: `2px solid ${theme.bg}`,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{
            fontSize: isMobile ? 22 : 28, fontWeight: 900, letterSpacing: '-0.04em',
          }}>{brand}<span style={{ color: theme.accent }}>.</span></div>
          {phoneDisplay && (
            <a href={formatTelHref(phoneDisplay)} style={{
              color: theme.bg, textDecoration: 'none',
              fontSize: isMobile ? 18 : 22, fontWeight: 900, letterSpacing: '-0.02em',
              display: 'inline-block', paddingBottom: 4,
              borderBottom: `3px solid ${theme.accent}`, alignSelf: 'flex-start',
            }}>{phoneDisplay}</a>
          )}
          {phoneDisplay && (
            <div style={{
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.18em', color: theme.bg, opacity: 0.7,
            }}>Mo - Fr 8 - 18 Uhr</div>
          )}
        </div>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 10 : 32,
        }}>
          {navLinks.map(l => (
            <Link key={l.label} href={l.href} style={{
              fontSize: isMobile ? 11 : 12, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.18em', color: theme.bg, textDecoration: 'none',
            }}>{l.label}</Link>
          ))}
        </div>
      </div>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 8 : 0,
        marginTop: isMobile ? 16 : 24,
      }}>
        <div style={{
          fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.18em', color: theme.bg, opacity: 0.7,
        }}>{`© ${new Date().getFullYear()} ${brand}`}</div>
        <div style={{
          fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.18em', color: theme.bg, opacity: 0.7,
        }}>{`Index ${String(new Date().getMonth() + 1).padStart(2, '0')}/${String(new Date().getFullYear()).slice(-2)} · Hannover`}</div>
      </div>
      <div style={{
        display: 'flex', flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 8 : 24, marginTop: isMobile ? 14 : 18,
      }}>
        {[
          { label: 'Impressum', href: '/impressum' },
          { label: 'Datenschutz', href: '/datenschutz' },
          { label: 'AGB', href: '/agb' },
          { label: 'Widerruf', href: '/widerruf' },
        ].map(l => (
          <Link key={l.href} href={l.href} style={{
            fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.18em', color: theme.bg, opacity: 0.7, textDecoration: 'none',
          }}>{l.label}</Link>
        ))}
      </div>
    </div>
  );
}
